import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import PageLayout, { PageHeader } from "@/components/PageLayout";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import PhoneInput from "@/components/PhoneInput";
import { 
  ArrowLeft, User, Mail, Phone, MapPin, Calendar, Camera,
  Edit, Save, Shield, CheckCircle, Loader2
} from "lucide-react";

export default function UserProfile() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    city: "",
    fiscal_code: "",
    birth_date: ""
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await authFetch(`${API}/user/profile`);
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setFormData({
          name: data.name || "",
          phone: data.phone || "",
          address: data.address || "",
          city: data.city || "",
          fiscal_code: data.fiscal_code || "",
          birth_date: data.birth_date || ""
        });
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API}/user/profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success("Profilo aggiornato!");
        setEditMode(false);
        fetchProfile();
      } else {
        toast.error("Errore nel salvataggio");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Seleziona un'immagine valida");
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'immagine deve essere inferiore a 5MB");
      return;
    }
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API}/user/profile-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success("Foto profilo aggiornata!");
        setProfile(prev => ({ ...prev, picture: data.picture }));
        // Update user context with new picture
        if (user) {
          login({ ...user, picture: data.picture });
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nel caricamento");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] dark:bg-[#1D1D1F] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#FF6B4A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <PageLayout>
      <PageHeader 
        title="Il mio Profilo"
        subtitle="Gestisci le tue informazioni personali"
        icon={User}
        iconColor="#FF6B4A"
        action={
          <Button
            onClick={() => editMode ? handleSave() : setEditMode(true)}
            disabled={saving}
            className={editMode ? "bg-emerald-500 hover:bg-emerald-600 rounded-xl" : "rounded-xl"}
            variant={editMode ? "default" : "outline"}
          >
            {editMode ? (
              <><Save size={16} className="mr-2" /> Salva</>
            ) : (
              <><Edit size={16} className="mr-2" /> Modifica</>
            )}
          </Button>
        }
      />

      <div className="max-w-2xl space-y-6">
        {/* Profile Picture */}
        <div className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10">
          <div className="flex items-center gap-5">
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] flex items-center justify-center overflow-hidden">
                {uploadingPhoto ? (
                  <Loader2 className="text-white animate-spin" size={28} />
                ) : profile?.picture ? (
                  <img src={profile.picture} alt="" className="w-full h-full rounded-2xl object-cover" />
                ) : (
                  <User className="text-white" size={36} />
                )}
              </div>
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
                data-testid="profile-photo-input"
              />
              {/* Camera button - always visible for changing photo */}
              <button 
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#1D1D1F] text-white flex items-center justify-center hover:bg-[#2D2D2F] transition-colors disabled:opacity-50"
                data-testid="change-photo-btn"
              >
                <Camera size={14} />
              </button>
              {/* Verified badge for Google/OAuth users */}
              {(user?.auth_provider === 'google' || user?.email_verified) && (
                <div 
                  className="absolute -top-1 -left-1 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center border-2 border-white dark:border-gray-800"
                  title="Account verificato"
                >
                  <CheckCircle className="text-white" size={14} />
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-bold text-[#1D1D1F] dark:text-white">{profile?.name || user?.name}</h2>
                {(user?.auth_provider === 'google' || user?.email_verified) && (
                  <span className="text-xs bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 rounded-full font-medium">
                    Verificato
                  </span>
                )}
              </div>
              <p className="text-[#86868B]">{profile?.email || user?.email}</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {user?.is_admin && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF6B4A]/10 text-[#FF6B4A] text-xs font-medium">
                    <Shield size={12} /> Admin
                  </span>
                )}
                {user?.auth_provider === 'google' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 text-xs font-medium">
                    Google
                  </span>
                )}
              </div>
              <p className="text-xs text-[#86868B] mt-1">Clicca sulla fotocamera per cambiare foto</p>
            </div>
          </div>
        </div>

        {/* Personal Info */}
        <div className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10">
          <h3 className="font-bold text-[#1D1D1F] dark:text-white mb-4">Informazioni Personali</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#86868B] mb-1">Nome completo</label>
              {editMode ? (
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="rounded-xl"
                />
              ) : (
                <p className="text-[#1D1D1F] dark:text-white">{formData.name || "-"}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#86868B] mb-1">Telefono</label>
              {editMode ? (
                <PhoneInput
                  value={formData.phone}
                  onChange={(fullNumber) => setFormData({...formData, phone: fullNumber})}
                  placeholder="333 1234567"
                />
              ) : (
                <p className="text-[#1D1D1F] dark:text-white">{formData.phone || "-"}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#86868B] mb-1">Codice Fiscale</label>
              {editMode ? (
                <Input
                  value={formData.fiscal_code}
                  onChange={(e) => setFormData({...formData, fiscal_code: e.target.value.toUpperCase()})}
                  placeholder="RSSMRA80A01H501Z"
                  maxLength={16}
                  className="rounded-xl uppercase"
                />
              ) : (
                <p className="text-[#1D1D1F] dark:text-white font-mono">{formData.fiscal_code || "-"}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#86868B] mb-1">Data di nascita</label>
              {editMode ? (
                <Input
                  type="date"
                  value={formData.birth_date}
                  onChange={(e) => setFormData({...formData, birth_date: e.target.value})}
                  className="rounded-xl"
                />
              ) : (
                <p className="text-[#1D1D1F] dark:text-white">
                  {formData.birth_date ? new Date(formData.birth_date).toLocaleDateString('it-IT') : "-"}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10">
          <h3 className="font-bold text-[#1D1D1F] dark:text-white mb-4">Indirizzo</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#86868B] mb-1">Indirizzo completo</label>
              {editMode ? (
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(address, placeData) => {
                    setFormData({
                      ...formData, 
                      address: address,
                      city: placeData?.city || formData.city
                    });
                  }}
                  placeholder="Cerca il tuo indirizzo..."
                />
              ) : (
                <p className="text-[#1D1D1F] dark:text-white">{formData.address || "-"}</p>
              )}
            </div>
          </div>
        </div>

        {/* Account Info */}
        <div className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10">
          <h3 className="font-bold text-[#1D1D1F] dark:text-white mb-4">Account</h3>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2">
              <span className="text-[#86868B]">Email</span>
              <span className="text-[#1D1D1F] dark:text-white">{user?.email}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-[#86868B]">Account verificato</span>
              <span className="flex items-center gap-1 text-emerald-600">
                <CheckCircle size={16} /> Sì
              </span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-[#86868B]">Membro da</span>
              <span className="text-[#1D1D1F] dark:text-white">
                {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('it-IT') : "-"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
